import { useMemo, useState } from 'react'
import { Button, DatePicker, Descriptions, Select, Space, Tag } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { CheckOutlined, CloseOutlined, DownloadOutlined, PaperClipOutlined, PrinterOutlined, SendOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import dayjs, { type Dayjs } from 'dayjs'
import { isRtl } from '@/app/i18n'
import { queryKeys } from '@/shared/api/queryKeys'
import { Can } from '@/shared/auth/Can'
import { useAuth } from '@/shared/auth/useAuth'
import { usePermissions } from '@/shared/auth/usePermissions'
import { rangeToIso, tenantToday } from '@/shared/lib/dayRange'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import { downloadCsv } from '@/shared/lib/download'
import { formatMoneyValue } from '@/shared/lib/money'
import { PageHeader } from '@/shared/ui/PageHeader'
import { DataTable } from '@/shared/ui/table/DataTable'
import { RowActions } from '@/shared/ui/table/RowActions'
import { useTableUrlState } from '@/shared/ui/table/useTableUrlState'
import {
  TRANSFER_STATES,
  confirmTransfer,
  fetchAllTransfers,
  fetchTransferPage,
  fetchTransferTotals,
  rejectTransfer,
  type CashTransfer,
  type TransferFilterKey,
  type TransferState,
} from '@/features/cashdesk/cashdeskApi'
import { CashAttachmentsModal, type AttachmentTarget } from '@/features/cashdesk/CashAttachmentsModal'
import { CashBranchSelect } from '@/features/cashdesk/CashBranchSelect'
import { CashDecisionModal, type CashDecision } from '@/features/cashdesk/CashDecisionModal'
import { CashFormModal } from '@/features/cashdesk/CashFormModal'
import { HeadOfficeCard } from '@/features/cashdesk/HeadOfficeCard'
import { referenceLabel, transferTypeName, useTransferTypes } from '@/features/cashdesk/transferTypeApi'
import { useCashBranches } from '@/features/cashdesk/useCashBranches'
import { useTransferSlipPrinter } from '@/features/cashdesk/useTransferSlipPrinter'

const STATE_COLOR: Record<TransferState, string> = {
  SENT: 'gold',
  CONFIRMED: 'green',
  REJECTED: 'red',
}

export function CashTransfersPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { canAny } = usePermissions()
  const queryClient = useQueryClient()
  const branches = useCashBranches()
  const { state: table, patch } = useTableUrlState()
  const filterValue = (key: TransferFilterKey) => table.filters[key]?.[0] ?? null
  const setFilters = (next: Partial<Record<TransferFilterKey, string | null>>) => {
    const filters: Record<string, readonly string[]> = { ...table.filters }
    for (const [key, value] of Object.entries(next)) {
      if (value) filters[key] = [value]
      else delete filters[key]
    }
    patch({ page: 1, filters })
  }
  const from = filterValue('from')
  const to = filterValue('to')
  const range: [Dayjs, Dayjs] | null = from && to ? [dayjs(from), dayjs(to)] : null
  const [creating, setCreating] = useState(false)
  const [decision, setDecision] = useState<CashDecision | null>(null)
  const [attachments, setAttachments] = useState<AttachmentTarget | null>(null)
  const transferTypes = useTransferTypes()
  const types = transferTypes.data
  const totals = useQuery({
    queryKey: queryKeys.cashdesk.transferTotals(table.filters),
    queryFn: ({ signal }) => fetchTransferTotals(table, signal),
  })
  const exportAll = useMutation({
    mutationFn: () => fetchAllTransfers(table),
    onSuccess: (rows) =>
      downloadCsv(`cash-transfers-${tenantToday().format('YYYY-MM-DD')}`, rows, [
        { header: t('cashdesk.transfer.sentAt'), value: (row) => formatDateTime(row.sentAt) },
        { header: t('cashdesk.transfer.reference'), value: (row) => row.reference },
        { header: t('cashdesk.transfer.from'), value: (row) => row.fromBranch.name },
        { header: t('cashdesk.transfer.to'), value: (row) => row.toNode.name },
        { header: t('cashdesk.transfer.type'), value: (row) => transferTypeName(row.transferType, types, t) },
        { header: t('cashdesk.transfer.externalReference'), value: (row) => row.externalReference ?? '' },
        { header: t('cashdesk.transfer.responsible'), value: (row) => row.responsiblePerson },
        { header: t('cashdesk.columns.amount'), value: (row) => formatMoneyValue(row.amount) },
        { header: t('cashdesk.columns.state'), value: (row) => t(`cashdesk.transferState.${row.state}`) },
        { header: t('cashdesk.transfer.sentBy'), value: (row) => row.sentBy.name },
        { header: t('cashdesk.transfer.decidedBy'), value: (row) => (row.decidedBy ? row.decidedBy.name : '') },
        { header: t('cashdesk.transfer.decidedAt'), value: (row) => (row.decidedAt ? formatDateTime(row.decidedAt) : '') },
        { header: t('cashdesk.transfer.remarks'), value: (row) => row.remarks ?? '' },
      ]),
  })
  const slip = useTransferSlipPrinter(types)
  const printSlip = slip.print

  const columns = useMemo<ColumnsType<CashTransfer>>(() => {
    const details = (row: CashTransfer) => {
      const type = types?.find((candidate) => candidate.code === row.transferType)
      return (
      <Descriptions size="small" column={1} bordered>
        <Descriptions.Item label={t('cashdesk.transfer.reference')}>{row.reference}</Descriptions.Item>
        <Descriptions.Item label={t('cashdesk.transfer.from')}>{row.fromBranch.name}</Descriptions.Item>
        <Descriptions.Item label={t('cashdesk.transfer.to')}>{row.toNode.name}</Descriptions.Item>
        <Descriptions.Item label={t('cashdesk.columns.amount')}>{formatMoneyValue(row.amount)}</Descriptions.Item>
        <Descriptions.Item label={t('cashdesk.transfer.type')}>
          {transferTypeName(row.transferType, types, t)}
        </Descriptions.Item>
        {row.externalReference ? (
          <Descriptions.Item label={type ? referenceLabel(type, t) : t('cashdesk.transfer.externalReference')}>
            {row.externalReference}
          </Descriptions.Item>
        ) : null}
        <Descriptions.Item
          label={t(row.externalReference ? 'cashdesk.transfer.depositedBy' : 'cashdesk.transfer.responsible')}
        >
          {row.responsiblePerson}
        </Descriptions.Item>
        <Descriptions.Item label={t('cashdesk.transfer.sentBy')}>
          {`${row.sentBy.name} · ${formatDateTime(row.sentAt)}`}
        </Descriptions.Item>
        {row.remarks ? (
          <Descriptions.Item label={t('cashdesk.transfer.remarks')}>{row.remarks}</Descriptions.Item>
        ) : null}
      </Descriptions>
      )
    }
    const own = (row: CashTransfer) => row.sentBy.id === user?.id

    return [
      {
        key: 'sentAt',
        title: t('cashdesk.columns.date'),
        width: 170,
        sorter: true,
        render: (_, row) => <span title={utcTooltip(row.sentAt)}>{formatDateTime(row.sentAt)}</span>,
      },
      {
        key: 'reference',
        title: t('cashdesk.transfer.reference'),
        width: 200,
        sorter: true,
        render: (_, row) => row.reference,
      },
      {
        key: 'transferType',
        title: t('cashdesk.transfer.type'),
        width: 170,
        sorter: true,
        render: (_, row) => transferTypeName(row.transferType, types, t),
      },
      {
        key: 'externalReference',
        title: t('cashdesk.transfer.externalReference'),
        width: 170,
        render: (_, row) =>
          row.externalReference ? (
            <span style={{ overflowWrap: 'anywhere' }}>{row.externalReference}</span>
          ) : (
            t('common.dash')
          ),
      },
      {
        key: 'state',
        title: t('cashdesk.columns.state'),
        width: 130,
        sorter: true,
        render: (_, row) => <Tag color={STATE_COLOR[row.state]}>{t(`cashdesk.transferState.${row.state}`)}</Tag>,
      },
      {
        key: 'amount',
        title: t('cashdesk.columns.amount'),
        width: 140,
        align: 'right',
        sorter: true,
        render: (_, row) => formatMoneyValue(row.amount),
      },
      {
        key: 'route',
        title: t('cashdesk.transfer.route'),
        width: 240,
        render: (_, row) => (
          <span style={{ overflowWrap: 'anywhere' }}>
            <bdi>{row.fromBranch.name}</bdi> {isRtl() ? '←' : '→'} <bdi>{row.toNode.name}</bdi>
          </span>
        ),
      },
      {
        key: 'responsible',
        title: t('cashdesk.transfer.responsibleAny'),
        width: 170,
        render: (_, row) => row.responsiblePerson,
      },
      { key: 'sentBy', title: t('cashdesk.transfer.sentBy'), width: 160, render: (_, row) => row.sentBy.name },
      {
        key: 'decision',
        title: t('cashdesk.transfer.decision'),
        width: 220,
        render: (_, row) =>
          row.decidedBy ? `${row.decidedBy.name} · ${formatDateTime(row.decidedAt)}` : t('common.dash'),
      },
      {
        key: 'actions',
        title: '',
        width: 56,
        align: 'center',
        fixed: 'right',
        render: (_, row) => (
          <RowActions
            items={[
              {
                key: 'attachments',
                label: t('cashdesk.attachment.open', { count: row.attachmentCount }),
                icon: <PaperClipOutlined />,
                onClick: () =>
                  setAttachments({
                    owner: 'transfers',
                    id: row.id,
                    title: row.reference,
                    canUpload: row.state === 'SENT' && canAny('cash.transfer.create'),
                  }),
              },
              {
                key: 'printSlip',
                label: t('cashdesk.transferSlip.print'),
                icon: <PrinterOutlined />,
                hidden: row.state !== 'CONFIRMED',
                onClick: () => printSlip(row),
              },
              {
                key: 'confirm',
                label: t('cashdesk.transfer.confirm'),
                icon: <CheckOutlined />,
                permission: 'cash.transfer.confirm',
                divider: true,
                hidden: row.state !== 'SENT' || own(row),
                onClick: () =>
                  setDecision({
                    title: t('cashdesk.transfer.confirmTitle'),
                    okText: t('cashdesk.transfer.confirm'),
                    danger: false,
                    warning: t('cashdesk.transfer.confirmWarn'),
                    reasonRequired: false,
                    details: details(row),
                    submit: async (text) => {
                      const confirmed = await confirmTransfer(row.id, text)
                      if (confirmed.state === 'CONFIRMED') printSlip(confirmed)
                    },
                  }),
              },
              {
                key: 'reject',
                label: t('cashdesk.transfer.reject'),
                icon: <CloseOutlined />,
                permission: 'cash.transfer.confirm',
                danger: true,
                hidden: row.state !== 'SENT' || own(row),
                onClick: () =>
                  setDecision({
                    title: t('cashdesk.transfer.rejectTitle'),
                    okText: t('cashdesk.transfer.reject'),
                    danger: true,
                    warning: t('cashdesk.transfer.rejectWarn'),
                    reasonRequired: true,
                    details: details(row),
                    submit: (text) => rejectTransfer(row.id, text ?? ''),
                  }),
              },
              {
                key: 'selfHint',
                label: t('cashdesk.selfDecisionHint'),
                permission: 'cash.transfer.confirm',
                divider: true,
                disabled: true,
                hidden: row.state !== 'SENT' || !own(row),
                onClick: () => undefined,
              },
            ]}
          />
        ),
      },
    ]
  }, [t, user?.id, canAny, types, printSlip])

  return (
    <>
      <PageHeader
        title={t('nav.cashTransfers')}
        description={t('pageIntro.cashTransfers')}
        extra={
          <Space>
            <Button icon={<DownloadOutlined />} loading={exportAll.isPending} onClick={() => exportAll.mutate()}>
              {t('cashdesk.transfer.exportAll')}
            </Button>
            <Can permission="cash.transfer.create">
              <Button type="primary" icon={<SendOutlined />} onClick={() => setCreating(true)}>
                {t('cashdesk.transfer.create')}
              </Button>
            </Can>
          </Space>
        }
      />

      <div style={{ marginBottom: 16 }}>
        <HeadOfficeCard />
      </div>

      <Space wrap style={{ marginBottom: 16 }}>
        <CashBranchSelect
          branches={branches.all}
          value={filterValue('branchId')}
          onChange={(branchId) => setFilters({ branchId })}
          loading={branches.isPending}
          allowClear
          placeholder={t('cashdesk.allBranches')}
        />
        <Select<string>
          style={{ minWidth: 170 }}
          value={filterValue('state') ?? 'ALL'}
          onChange={(value) => setFilters({ state: value === 'ALL' ? null : value })}
          options={[
            { value: 'ALL', label: t('cashdesk.allStates') },
            ...TRANSFER_STATES.map((s) => ({ value: s, label: t(`cashdesk.transferState.${s}`) })),
          ]}
        />
        <Select<string>
          allowClear
          style={{ minWidth: 200 }}
          placeholder={t('cashdesk.transfer.allTypes')}
          value={filterValue('transferType') ?? undefined}
          onChange={(value) => setFilters({ transferType: value ?? null })}
          options={(types ?? []).map((type) => ({ value: type.code, label: transferTypeName(type.code, types, t) }))}
        />
        <DatePicker.RangePicker
          allowClear
          value={range}
          onChange={(value) => {
            const span = value && value[0] && value[1] ? rangeToIso([value[0], value[1]]) : null
            setFilters({ from: span?.from ?? null, to: span?.to ?? null })
          }}
          disabledDate={(d) => d.isAfter(tenantToday(), 'day')}
        />
      </Space>

      <DataTable<CashTransfer>
        queryKey={queryKeys.cashdesk.transfers}
        fetchPage={fetchTransferPage}
        columns={columns}
        rowKey={(row) => row.id}
        tableId="cash-transfers"
        scrollX={1840}
        stickyFirstColumn
        showTotal={false}
        summary={() =>
          totals.data
            ? [
                {
                  key: 'total',
                  cells: {
                    sentAt: <strong>{t('cashdesk.transfer.totalFiltered', { count: totals.data.transfers })}</strong>,
                    state: totals.data.byState
                      .map((row) => `${t(`cashdesk.transferState.${row.state}`)}: ${formatMoneyValue(row.amount)}`)
                      .join(' · '),
                    amount: <strong>{formatMoneyValue(totals.data.amount)}</strong>,
                  },
                },
              ]
            : []
        }
        emptyTitle={t('cashdesk.transfer.empty')}
      />

      <CashFormModal mode={creating ? 'transfer' : null} onClose={() => setCreating(false)} />
      <CashDecisionModal
        decision={decision}
        onClose={() => setDecision(null)}
        onDone={() => {
          setDecision(null)
          void queryClient.invalidateQueries({ queryKey: queryKeys.cashdesk.all })
        }}
      />
      <CashAttachmentsModal target={attachments} onClose={() => setAttachments(null)} />
      {slip.portal}
    </>
  )
}
